/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React, { useEffect } from "react";
import cn from "classnames";
import styles from "gui/visual/visual.module.css";
import truncate from "lodash/truncate";
import { Chart } from "gui/chart/chart";
import { FullDashboard } from "lib/entries/types";
import { QueryResult, QueryResultMatrix } from "lib/results/types";
import { QueryResultColumn } from "lib/results/types";
import { QueryResultRow } from "lib/results/types";
import { QueryVisual } from "lib/visuals/types";
import { TableFooter, TableResults } from "gui/table/table";
import { TableRow } from "gui/table/table";
import { Visual } from "lib/visuals/types";
import { VisualChart } from "lib/visuals/types";
import { VisualCounter } from "lib/visuals/types";
import { VisualHeader } from "gui/visual/header";
import { FooterOwner } from "./FooterOwner";
import { VisualTable } from "lib/visuals/types";
import { VisualTableColumn } from "lib/visuals/types";
import { VisualText } from "lib/visuals/types";
import { formatCounterLabel, getCounterValue } from "lib/counter/counter";
import { formatCounterValue } from "lib/counter/counter";
import { formatDateYMDHM } from "lib/dates/dates";
import { formatNumber } from "lib/numbers/numbers";
import { isParsableDate } from "lib/dates/dates";
import { isVisualChart } from "lib/visuals/visuals";
import { isVisualCounter } from "lib/visuals/visuals";
import { isVisualTable } from "lib/visuals/visuals";
import { isVisualText } from "lib/visuals/visuals";
import { isVisualParam } from "lib/visuals/visuals";
import { logger } from "lib/logger/browser";
import { resultPrimitive } from "lib/results/values";
import { resultStatusType } from "lib/results/status";
import { resultStatusError } from "lib/results/status";
import { sanitizeHTML } from "lib/html/html";
import { Icon } from "gui/icon/icon";
import { StatusIcon } from "gui/visual/status";
import { QueryVizScheduleBadge } from "./QueryVizScheduleBadge";
import ResizeObserver from "resize-observer-polyfill";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { Param } from "gui/parameters/parameters";
import { Parameter, ParameterMerge } from "lib/parameters/types";
import { isNumber } from "lib/types/types";
import { set } from "lodash";
import { getMaxMin } from "lib/math/math";
import { useAnalytics } from "../analytics/analytics";
import { AnchorButtonTextOnly } from "components/ButtonTextOnly/AnchorButtonTextOnly";
import { RefreshQueryButton } from "./RefreshQueryButton/RefreshQueryButton";
import * as Sentry from "@sentry/react";
import { FixQuery } from "gui/editor/Wand/FixQuery";
import ReactHtmlParser from "react-html-parser";

export interface VisualProps<T extends Visual = Visual> {
  className?: string;
  visual: T;
  watermark: boolean;
  param?: Parameter;
  parameters?: ParameterMerge;
  queryId?: number;
  queryName?: string;
  queryDescription?: string;
  dashboard?: FullDashboard;
  title?: string;
  owner?: {
    type: "team" | "user";
    handle: string;
    profile_image_url: string | null;
  };
  result?: QueryResult;
  iframe?: boolean;
  transparent?: boolean;
  edit?: boolean;
  search?: boolean;
}

// Extension of the visual props to add VisualItem component
// specific context
export interface VisualItemProps<T extends Visual = Visual>
  extends VisualProps<T> {
  containerRef: React.RefObject<HTMLDivElement>;
  showProfileDetails: boolean;
}

export const VisualItem: React.FC<VisualProps> = (props) => {
  const { visual, result } = props;
  const statusType = resultStatusType(result);
  const statusError = resultStatusError(result);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const showProfileDetails = useShowProfileDetails(containerRef);
  const visualItemProps: VisualItemProps<typeof props.visual> = {
    ...props,
    showProfileDetails,
    containerRef,
  };
  const { captureEvent } = useAnalytics();

  useEffect(() => {
    if (statusType !== "unknown") {
      captureEvent(`VisualItem viewed`, { statusType });
    }
  }, [statusType]);

  if (isVisualText(visual)) {
    return <ItemText {...visualItemProps} visual={visual} />;
  }
  if (isVisualParam(visual)) {
    return <Param parameter={props.param!} parameters={props.parameters!} />;
  }
  // If it is not a param of a text widget then it must be expected to show the
  // the results of a query. Therefore check the query status type to decide what to
  // render
  switch (statusType) {
    case "initialLoading":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          Loading
        </ItemMessage>
      );
    case "initialQueued":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          {`Queued (${result?.job?.queue_position})`}
          <br />
          <p>
            Try using a larger query engine for better performance.{" "}
            <a
              href="https://dune.com/docs/query/query-executions/?h=medium#query-engine-size"
              target="_blank"
              className={styles.learnMoreLink}
            >
              {" "}
              Learn more here
            </a>
            .
          </p>
        </ItemMessage>
      );
    case "initialRunning":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          Running
        </ItemMessage>
      );
    case "unknown":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          Click "Run" to get results
        </ItemMessage>
      );
    case "error":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          {renderMessageWithLinks(statusError)}
          <FixQuery result={result} />
        </ItemMessage>
      );
    case "emptyResults":
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          No results from query.
        </ItemMessage>
      );
    case "complete":
    case "errorWithStaleResult":
    case "refreshRunning":
    case "refreshQueued":
      if (isVisualChart(visual)) {
        return <ItemChart {...visualItemProps} visual={visual} />;
      }

      if (isVisualCounter(visual)) {
        return <ItemCounter {...visualItemProps} visual={visual} />;
      }

      if (isVisualTable(visual)) {
        return <ItemTable {...visualItemProps} visual={visual} />;
      }
      return (
        <ItemMessage {...visualItemProps} visual={visual}>
          Unsupported visualization ({visual["type"]}).
        </ItemMessage>
      );
  }
};

const ItemMessage: React.FC<VisualItemProps<QueryVisual>> = (props) => {
  return (
    <ItemWrap {...props} className={styles.message}>
      <ItemResult {...props}>
        <div className={styles.resultMessage}>{props.children}</div>
      </ItemResult>
      <div className={styles.vizFooter}>
        <div className={styles.ownerStatuses}>
          <FooterOwner {...props} />
          <div className={styles.statuses}>
            <RefreshQueryButton {...props} />
            <QueryVizScheduleBadge queryDetails={props.visual.query_details} />
            <StatusIcon {...props} />
          </div>
        </div>
      </div>
    </ItemWrap>
  );
};

const ItemText: React.FC<VisualItemProps<VisualText>> = (props) => {
  return (
    <ItemWrap {...props}>
      {props.visual.html && (
        <div className={styles.html}>
          {ReactHtmlParser(props.visual.html, {
            transform: (node) => {
              if (node?.type === "tag" && node?.name === "a") {
                node.attribs.target = "_blank";
              }
            },
          })}
        </div>
      )}
    </ItemWrap>
  );
};

const ItemCounter: React.FC<VisualItemProps<VisualCounter>> = (props) => {
  const value = getCounterValue(props.visual, props.result);
  const formattedValue = formatCounterValue(props.visual, value);
  const label = formatCounterLabel(props.visual);

  // Fit the counter text to its parent element by rendering the text in an
  // SVG element, and then adjusting the SVG viewBox based on text length.
  const width = Math.max(formattedValue.length * 40, label.length * 15, 350);
  const height = width / 5;

  const getFillColor = () => {
    if (isNumber(value)) {
      if (props.visual.options.coloredPositiveValues && value > 0) {
        return "var(--success-green)";
      }

      if (props.visual.options.coloredNegativeValues && value < 0) {
        return "var(--danger-red)";
      }
    }

    return undefined;
  };

  return (
    <ItemWrap {...props}>
      <ItemResult {...props}>
        {props.watermark && (
          <img
            src="/assets/glyph.svg"
            className={styles.watermark}
            aria-hidden
            alt=""
          />
        )}
        <svg viewBox={`0 0 ${width} ${height}`}>
          {formattedValue && (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline={label ? "bottom" : "middle"}
              fontSize="3em"
              fill={getFillColor()}
            >
              {formattedValue}
            </text>
          )}
          {label && (
            <text
              x="50%"
              y="85%"
              textAnchor="middle"
              dominantBaseline={formattedValue ? "top" : "middle"}
              fontSize="1.5em"
            >
              {label}
            </text>
          )}
        </svg>
      </ItemResult>
      <div className={styles.vizFooter}>
        <div className={styles.ownerStatuses}>
          <FooterOwner {...props} />
          <div className={styles.statuses}>
            <RefreshQueryButton {...props} />
            <QueryVizScheduleBadge queryDetails={props.visual.query_details} />
            <StatusIcon {...props} />
          </div>
        </div>
      </div>
    </ItemWrap>
  );
};

const ItemChart: React.FC<VisualItemProps<VisualChart>> = (props) => {
  const rows = props.result?.matrix?.rows ?? [];

  return (
    <ItemWrap {...props}>
      <ItemResult stretch {...props}>
        {props.result && rows.length > 0 && (
          <Chart
            visual={props.visual}
            result={props.result}
            watermark={props.watermark}
          />
        )}
      </ItemResult>
      <div className={styles.vizFooter}>
        <div className={styles.ownerStatuses}>
          <FooterOwner {...props} />
          <div className={styles.statuses}>
            <RefreshQueryButton {...props} />
            <QueryVizScheduleBadge queryDetails={props.visual.query_details} />
            <StatusIcon {...props} />
          </div>
        </div>
      </div>
    </ItemWrap>
  );
};

// Function returns additional data needed to render a Table Cell
// Ex. Progressbar displays value in percentage. In order to get this value it has to know max and min value from all the rows.
export const getAdditionalTableCellData = (
  options: VisualTable["options"],
  rows: QueryResultRow[] | undefined
) => {
  const cellsData: Record<string, TableCellData> = {};

  if (rows) {
    options.columns?.forEach((option) => {
      if (option.type === "progressbar") {
        const rowValues = rows.map((columns) => {
          // Because queries can return number values (base10) as strings
          // we call resultPrimitive to get actual type/value of the column
          const value = resultPrimitive(columns[option.name]);
          return isNumber(value) ? value : 0;
        });

        const [maxValue, minValue] = getMaxMin(rowValues);

        set(cellsData, [option.name], {
          getCellValueInPercentage: (value: number) => {
            const max = Math.abs(value < 0 ? minValue : maxValue);
            const normalizedValue = Math.abs(value);

            return normalizedValue > 0 && max > 0
              ? (normalizedValue / max) * 100
              : 0;
          },
        });
      }
    });
  }

  return cellsData;
};

const EMPTY_COLUMN_NAME_ID = "5847052339_dune_unnamed";

const ItemTable: React.FC<VisualItemProps<VisualTable>> = (props) => {
  const options = props.visual.options;
  const rows = props.result?.matrix?.rows;
  const maxResultSizeReachedBytes =
    props.result?.meta?.max_result_size_reached_bytes;
  const requestResultSizeBytes =
    props.result?.meta?.request_max_result_size_bytes;
  const visibleColumns = React.useMemo(() => {
    const cellsData = getAdditionalTableCellData(options, rows);

    const matrix = props.result?.matrix;

    return matrix?.columns
      .map((column) => ({
        id: column.name || EMPTY_COLUMN_NAME_ID,
        accessor: column.name,
        sortType: getSortByColumnFunction(
          matrix,
          column.name || EMPTY_COLUMN_NAME_ID
        ),
        Header: tableHeader(props.visual, column),
        Cell: tableCell({
          visual: props.visual,
          column,
          options: options.columns?.find((col) => col.name === column.name),
          cellData: cellsData[column.name],
        }),
      }))
      .filter(
        (column) =>
          options.columns?.find((col) => col.name === column.accessor)
            ?.isHidden !== true
      );
  }, [props.visual, props.result, options.columns]);

  if (rows && visibleColumns) {
    const tableOptions = {
      columns: visibleColumns,
      data: rows,
      initialState: { pageSize: options?.itemsPerPage || 25 },
    };

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      prepareRow,
      setGlobalFilter,
      page,
      pageOptions,
      pageCount,
      gotoPage,
      state: { pageIndex, globalFilter },
    } = useTable(tableOptions, useGlobalFilter, useSortBy, usePagination);

    return (
      <ItemWrap {...props}>
        <ItemResult {...props}>
          {rows && visibleColumns && (
            <TableResults
              getTableProps={getTableProps}
              headerGroups={headerGroups}
              getTableBodyProps={getTableBodyProps}
              page={page}
              prepareRow={prepareRow}
            />
          )}
        </ItemResult>
        <div className={styles.vizFooter}>
          <TableFooter
            columns={visibleColumns}
            globalFilter={globalFilter}
            setGlobalFilter={setGlobalFilter}
            pageIndex={pageIndex}
            pageOptions={pageOptions}
            pageCount={pageCount}
            gotoPage={gotoPage}
            search={props.search ?? true}
            total={rows.length}
            maxResultSizeReached={Boolean(maxResultSizeReachedBytes)}
            requestResultSizeBytes={requestResultSizeBytes}
          />
          <div className={styles.ownerStatuses}>
            <FooterOwner {...props} />
            <div className={styles.statuses}>
              <RefreshQueryButton {...props} />
              <QueryVizScheduleBadge
                queryDetails={props.visual.query_details}
              />
              <StatusIcon {...props} />
            </div>
          </div>
        </div>
      </ItemWrap>
    );
  }
  return <ItemMessage {...props}>No results from query.</ItemMessage>;
};

const ItemWrap: React.FC<VisualItemProps> = (props) => {
  const className = cn(
    styles.wrap,
    props.transparent && styles.transparent,
    isVisualText(props.visual) && styles.text,
    isVisualCounter(props.visual) && styles.counter,
    isVisualChart(props.visual) && styles.chart,
    isVisualTable(props.visual) && styles.table,
    props.className
  );

  return (
    <div ref={props.containerRef} className={className}>
      {props.children}
    </div>
  );
};

const ItemResult: React.FC<
  VisualItemProps<QueryVisual> & { stretch?: boolean }
> = (props) => {
  return (
    <>
      <VisualHeader {...props} />
      <div
        className={cn(
          styles.result,
          props.stretch && styles["stretch-results"]
        )}
      >
        {props.children}
      </div>
    </>
  );
};

interface TableProgressBarProps {
  percentage: number;
  className?: string;
  children?: React.ReactNode;
}

const TableProgressBar = ({
  percentage,
  children,
  className,
}: TableProgressBarProps) => {
  return (
    <div
      className={cn(styles.progressbar, className)}
      style={
        {
          "--progress": `${percentage}%`,
        } as React.CSSProperties
      }
    >
      <div />
      {children}
    </div>
  );
};

const tableHeader = (visual: VisualTable, column: QueryResultColumn) => () => {
  return (
    <div className={tableClass(visual, column)}>
      {tableColumnOptions(visual, column)?.title || column.name}
    </div>
  );
};

interface TableCellData {
  getCellValueInPercentage?: (value: number) => number;
}

interface TableCell {
  visual: VisualTable;
  column: QueryResultColumn;
  options?: VisualTableColumn;
  cellData?: TableCellData;
}

const tableCell = ({ visual, column, options, cellData }: TableCell) => (data: {
  row: TableRow<QueryResultRow>;
}) => {
  const { getCellValueInPercentage } = cellData || {};
  const value = resultPrimitive(data.row.original[column.name]);

  if (
    options?.type === "progressbar" &&
    getCellValueInPercentage &&
    isNumber(value)
  ) {
    return (
      <TableProgressBar
        percentage={getCellValueInPercentage(value)}
        className={cn(
          tableClass(visual, column),
          getCellColorClassName(options, value),
          value < 0 && styles.negative
        )}
      >
        {tableValue(visual, column, data.row.original)}
      </TableProgressBar>
    );
  }

  return (
    <div
      className={cn(
        tableClass(visual, column),
        isNumber(value) && getCellColorClassName(options, value)
      )}
    >
      {tableValue(visual, column, data.row.original)}
    </div>
  );
};

const tableClass = (visual: VisualTable, column: QueryResultColumn) => {
  const options = tableColumnOptions(visual, column);

  return cn(
    column.type === "datetime" && styles.datetime,
    options?.alignContent === "left" && styles.left,
    options?.alignContent === "right" && styles.right,
    options?.alignContent === "center" && styles.center
  );
};

const tableValue = (
  visual: VisualTable,
  column: QueryResultColumn,
  row: QueryResultRow
) => {
  const value = row[column.name];
  const p = resultPrimitive(value);

  if (p === "") {
    // Also happens for empty array – resultPrimitive([]) -> ""
    return <span className={styles.empty} aria-hidden />;
  }

  if (value && Array.isArray(value)) {
    return <TableValueList value={p} />;
  }

  if (p && typeof p === "number") {
    return formatNumber(p, tableColumnOptions(visual, column)?.numberFormat);
  }

  // Format date-like strings as formatted date values.
  if (p && typeof p === "string" && isParsableDate(p)) {
    return formatDateYMDHM(p);
  }

  // Attempt to parse text cells as HTML to match Redash's behaviour.
  if (p && typeof p === "string") {
    try {
      return <TableValueHTML value={p} />;
    } catch (err) {
      logger.warn("failed to parse table html:", p);
    }
  }

  return p;
};

const TableValueList: React.FC<{
  value: string | number | null;
}> = (props) => {
  const long = `${props.value}`;
  const short = truncate(long, { length: 50, omission: "" });
  const canExpand = long.length !== short.length;

  const [expand, setExpand] = React.useState(false);
  const toggleExpand = () => setExpand((prev) => !prev);

  return (
    <div className={styles.tableListValue}>
      {expand ? long : short}
      {!expand && canExpand && (
        <button
          onClick={toggleExpand}
          aria-label="Expand"
          className={styles.expandButton}
        >
          <small>&bull;&bull;&bull;</small>
        </button>
      )}
      {expand && canExpand && (
        <button
          onClick={toggleExpand}
          aria-label="Contract"
          className={styles.contractButton}
        >
          <Icon icon="arrow-left" />
        </button>
      )}
    </div>
  );
};

const TableValueHTML: React.FC<{
  value: string;
}> = (props) => {
  return (
    <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(props.value) }} />
  );
};

const tableColumnOptions = (visual: VisualTable, column: QueryResultColumn) => {
  return visual.options?.columns?.find((c: VisualTableColumn) => {
    return c.name === column.name;
  });
};

/** Sorting **/
type SortType = (
  a: TableRow<QueryResultRow>,
  b: TableRow<QueryResultRow>,
  columnId: string
) => number;

function getSortByColumnFunction(
  matrix: QueryResultMatrix,
  columnId: string
): SortType {
  // TODO: Once the data model informs which data type each column
  // contains, use that type to determine correct sort function.
  // See progress in: https://dune.height.app/T-23350
  // For now, sort as numbers if all values are number or null.
  const onlyNumbers = matrix?.rows.every((row) => {
    const primitive = resultPrimitive(row[columnId]);
    return primitive === null || typeof primitive === "number";
  });

  return onlyNumbers ? sortByNumberColumn : sortByStringColumn;
}

const sortByNumberColumn: SortType = (a, b, columnId) => {
  if (columnId === EMPTY_COLUMN_NAME_ID) {
    columnId = "";
  }
  const oa = a.original[columnId];
  const ob = b.original[columnId];

  return Number(oa) - Number(ob);
};

const sortByStringColumn: SortType = (a, b, columnId) => {
  if (columnId === EMPTY_COLUMN_NAME_ID) {
    columnId = "";
  }
  const oa = a.original[columnId];
  const ob = b.original[columnId];

  return String(oa).localeCompare(String(ob));
};

// When the width of the visual item container is smaller than this
// number we should hide the user information.
// This number is just large enough to ensure that on most
// mobile phones we show the user profile image
const MIN_WIDTH_TO_SHOW_USER_DETAILS = 330;

// This hook expects the visual item container ref to be passed in.
// It will subscribe to size changes of the element and when the size
// of the element is smaller than a certain size will return false.
const useShowProfileDetails = (
  containerRef: React.RefObject<HTMLDivElement | null>
): boolean => {
  const [shouldShow, setShouldShow] = React.useState<boolean>(false);

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver((entries = []) => {
      entries.forEach((entry) => {
        const { width } = entry.contentRect;
        setShouldShow(width > MIN_WIDTH_TO_SHOW_USER_DETAILS);
      });
    });

    if (containerRef && containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef.current]);

  return shouldShow;
};

type ColoredValues = {
  coloredPositiveValues: boolean;
  coloredNegativeValues: boolean;
};

const getCellColorClassName = <T extends Partial<ColoredValues>>(
  coloredValues: T | undefined,
  value: number
) =>
  cn(
    coloredValues?.coloredPositiveValues && value > 0 && styles.coloredPositive,
    coloredValues?.coloredNegativeValues && value < 0 && styles.coloredNegative
  );

export const renderMessageWithLinks = (message?: string) => {
  // Customized regex from https://uibakery.io/regex-library/url
  // Regex to match links in a string. Handle special cases when "." or "?" are at the end of the link (mark as the end of the sentence)
  const linkRegex = /\b(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+~#&/=]|([?.]\S))*)|(www\.[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+~#&/=]|[?.]\S)*)/gi;

  if (!message) {
    return null;
  }

  try {
    return (
      <span>
        {message
          .split(linkRegex)
          .filter((part) => part)
          .map((part, index) => {
            if (part.match(linkRegex)) {
              return (
                <AnchorButtonTextOnly
                  key={`${part}-${index}`}
                  size="S"
                  theme="secondary"
                  href={part}
                  target="_blank"
                >
                  {part}
                </AnchorButtonTextOnly>
              );
            }
            return (
              <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
            );
          })}
      </span>
    );
  } catch (err) {
    Sentry.captureException(
      `Failed to correctly parse query error message, ${err}`
    );

    return message;
  }
};
